import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  getStoreSettings,
  updateStoreSettings,
  type StoreSettings,
} from '../api/waiting.api';
import Layout from '../components/layout/Layout';
import StoreImageCropper from '../components/settings/StoreImageCropper';
import styles from './SettingsPage.module.css';

export default function StoreSettingsPage() {
  const { storeId } = useAuth();

  const [openTime, setOpenTime] = useState('11:00');
  const [closeTime, setCloseTime] = useState('22:00');
  const [lastOrderTime, setLastOrderTime] = useState('21:00');
  const [waitingClosedMessage, setWaitingClosedMessage] = useState(
    '금일 대기가 마감되었습니다',
  );
  const [averageMinutesPerTeam, setAverageMinutesPerTeam] = useState(10);
  const [takeoutEnabled, setTakeoutEnabled] = useState(false);
  const [averageMinutesPerTakeout, setAverageMinutesPerTakeout] = useState(15);
  const [remoteWaitingEnabled, setRemoteWaitingEnabled] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadSettings = useCallback(async () => {
    if (!storeId) return;
    try {
      setIsLoading(true);
      const settings: StoreSettings = await getStoreSettings(storeId);
      if (settings.businessHours) {
        if (settings.businessHours.open) setOpenTime(settings.businessHours.open);
        if (settings.businessHours.close) setCloseTime(settings.businessHours.close);
        if (settings.businessHours.lastOrder) setLastOrderTime(settings.businessHours.lastOrder);
      }
      if (typeof settings.waitingClosedMessage === 'string') {
        setWaitingClosedMessage(settings.waitingClosedMessage);
      }
      if (typeof settings.averageMinutesPerTeam === 'number') {
        setAverageMinutesPerTeam(settings.averageMinutesPerTeam);
      }
      if (typeof settings.takeoutEnabled === 'boolean') {
        setTakeoutEnabled(settings.takeoutEnabled);
      }
      if (typeof settings.averageMinutesPerTakeout === 'number') {
        setAverageMinutesPerTakeout(settings.averageMinutesPerTakeout);
      }
      // remoteWaitingEnabled 는 default true (undefined 도 true 로 처리)
      setRemoteWaitingEnabled(settings.remoteWaitingEnabled !== false);
    } catch {
      /* 기본값 유지 */
    } finally {
      setIsLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSave = async () => {
    if (!storeId || isSaving) return;
    setIsSaving(true);
    setSaveMessage(null);
    try {
      await updateStoreSettings(storeId, {
        businessHours: {
          open: openTime,
          close: closeTime,
          lastOrder: lastOrderTime,
        },
        waitingClosedMessage,
        averageMinutesPerTeam,
        takeoutEnabled,
        averageMinutesPerTakeout,
        remoteWaitingEnabled,
      });
      setSaveMessage('설정이 저장되었습니다.');
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err) {
      setSaveMessage(
        err instanceof Error ? err.message : '저장 중 오류가 발생했습니다.',
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Layout title="매장 설정">
      <div className={styles.page}>
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>매장 정보</h2>
          <div className={styles.infoRow}>
            <span className={styles.infoLabel}>매장 ID</span>
            <span className={styles.infoValue}>{storeId || '-'}</span>
          </div>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>매장 이미지 (16:9)</h2>
          {storeId && <StoreImageCropper storeId={storeId} />}
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>영업시간 설정</h2>
          {isLoading ? (
            <div className={styles.loadingText}>로딩 중...</div>
          ) : (
            <>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>오픈 시간</span>
                <input
                  type="time"
                  className={styles.timeInput}
                  value={openTime}
                  onChange={(e) => setOpenTime(e.target.value)}
                />
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>마감 시간</span>
                <input
                  type="time"
                  className={styles.timeInput}
                  value={closeTime}
                  onChange={(e) => setCloseTime(e.target.value)}
                />
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>라스트오더</span>
                <input
                  type="time"
                  className={styles.timeInput}
                  value={lastOrderTime}
                  onChange={(e) => setLastOrderTime(e.target.value)}
                />
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>대기마감 메시지</span>
                <input
                  type="text"
                  className={styles.textInput}
                  value={waitingClosedMessage}
                  onChange={(e) => setWaitingClosedMessage(e.target.value)}
                  placeholder="금일 대기가 마감되었습니다"
                />
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>매장식사 예상 대기시간(분)</span>
                <input
                  type="number"
                  className={styles.textInput}
                  value={averageMinutesPerTeam}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    if (!isNaN(val) && val > 0) setAverageMinutesPerTeam(val);
                  }}
                  min={1}
                  max={120}
                  placeholder="10"
                  style={{ width: '80px' }}
                />
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>포장 예상 대기시간(분)</span>
                <input
                  type="number"
                  className={styles.textInput}
                  value={averageMinutesPerTakeout}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    if (!isNaN(val) && val > 0) setAverageMinutesPerTakeout(val);
                  }}
                  min={1}
                  max={120}
                  placeholder="15"
                  disabled={!takeoutEnabled}
                  style={{ width: '80px', opacity: takeoutEnabled ? 1 : 0.5 }}
                />
                {!takeoutEnabled && (
                  <span style={{ fontSize: '12px', color: '#94a3b8', marginLeft: '8px' }}>
                    대기관리에서 포장을 활성화하세요
                  </span>
                )}
              </div>
              <div className={styles.infoRow}>
                <span className={styles.infoLabel}>원격 줄서기 (먹킷 등)</span>
                <label
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={remoteWaitingEnabled}
                    onChange={(e) => setRemoteWaitingEnabled(e.target.checked)}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '14px', color: 'var(--color-text)' }}>
                    {remoteWaitingEnabled ? '활성 (외부 앱에서 원격 등록 가능)' : '비활성 (매장 방문 직접 등록만)'}
                  </span>
                </label>
              </div>
              <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '-6px', marginBottom: '12px', paddingLeft: 'calc(140px + 16px)' }}>
                먹킷 앱 등에서 손님이 매장 방문 전에 미리 대기 등록하는 기능을 켜고 끕니다. OFF 시 키오스크 직접 등록만 가능합니다.
              </div>
              <button
                type="button"
                className={styles.saveButton}
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? '저장 중...' : '설정 저장'}
              </button>
              {saveMessage && (
                <div className={styles.saveMessage}>{saveMessage}</div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
